import { useEffect, useRef, useState } from 'react'
import { HexColorPicker } from 'react-colorful'
import { Pipette } from 'lucide-react'
import { cn } from './cn'
import { Popover } from './menu'
import { useDraftError, useFieldControl } from './field'
import { INVALID_DRAFT_ATTR, isCommitKey } from './draft'
import { Input } from './input'

export const DEFAULT_SWATCHES = [
  '#111111', '#4b5563', '#9ca3af', '#ffffff', '#b91c1c', '#ea580c', '#d97706', '#65a30d',
  '#059669', '#0891b2', '#2563eb', '#4f46e5', '#7c3aed', '#c026d3', '#db2777', '#f5e6d3',
]

const HEX = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i
/** Normalise "#abc" / "abc" / "#AABBCC" → "#aabbcc"; null when invalid. */
export function normalizeHex(v: string): string | null {
  const m = HEX.exec(v.trim())
  if (!m) return null
  const h = m[1].length === 3 ? m[1].split('').map(c => c + c).join('') : m[1]
  return `#${h.toLowerCase()}`
}

export interface ColorPickerProps {
  value: string | null
  onChange: (hex: string) => void
  swatches?: string[]
  disabled?: boolean
  id?: string
  className?: string
  /** Compact swatch-only trigger (theme editor lists). */
  compact?: boolean
}

const HEX6 = /^#?[0-9a-f]{6}$/i

/**
 * Colour field: swatch + hex input; the popover has a picker, preset swatches and the eyedropper
 * (where supported). Always emits "#rrggbb". `<ColorPicker value={brand} onChange={setBrand} />`
 * Typing emits as soon as six hex digits are in; a short "#abc" is expanded on blur, Enter or ⌘S;
 * anything else reverts on blur (and blocks a save while it's still in the box).
 */
export function ColorPicker({ value, onChange, swatches = DEFAULT_SWATCHES, disabled, id, className, compact }: ColorPickerProps) {
  const f = useFieldControl()
  const color = normalizeHex(value ?? '') ?? '#000000'
  const [text, setText] = useState(value ?? '')
  const [focused, setFocused] = useState(false)
  const focusedRef = useRef(false)
  // Never overwrite what the merchant is typing: sync from `value` only while the box isn't focused.
  useEffect(() => { if (!focusedRef.current) setText(value ?? '') }, [value])
  const trimmed = text.trim()
  const draftError = (trimmed === '' ? value !== null && value !== '' : !normalizeHex(trimmed)) ? 'Enter a hex colour like #1a2b3c' : null
  useDraftError(focused ? null : draftError)
  const commitText = () => {
    const h = normalizeHex(trimmed)
    if (h) { if (h !== normalizeHex(value ?? '')) onChange(h); setText(h) }
    return !!h
  }
  const eyedropper = typeof window !== 'undefined' && 'EyeDropper' in window
  const pickFromScreen = async () => {
    try {
      const ED = (window as any).EyeDropper
      const r = await new ED().open()
      const hex = normalizeHex(r?.sRGBHex ?? '')
      if (hex) onChange(hex)
    } catch { /* cancelled */ }
  }
  const swatch = (
    <button type="button" disabled={disabled} aria-label={`Choose colour, current ${color}`}
      className={cn('relative size-6 shrink-0 overflow-hidden rounded-[6px] border border-black/10 shadow-[inset_0_0_0_1px_rgb(255_255_255/0.15)] outline-none focus-visible:ring-2 focus-visible:ring-focus dark:border-white/15',
        compact && 'size-8 rounded-control')}
      style={{ background: color }} />
  )
  const panel = (
    <div className="flex flex-col gap-3">
      <HexColorPicker color={color} onChange={c => onChange(c.toLowerCase())} className="!w-full [&_.react-colorful__saturation]:rounded-t-[8px] [&_.react-colorful__hue]:mt-2 [&_.react-colorful__hue]:h-3 [&_.react-colorful__hue]:rounded-full [&_.react-colorful__last-control]:rounded-full" />
      <div className="grid grid-cols-8 gap-2">
        {swatches.map(s => (
          <button key={s} type="button" aria-label={s} onClick={() => onChange(normalizeHex(s) ?? s)}
            className={cn('aspect-square rounded-[6px] border border-black/10 outline-none transition-transform hover:scale-110 focus-visible:ring-2 focus-visible:ring-focus dark:border-white/15',
              normalizeHex(s) === color && 'ring-2 ring-primary ring-offset-1 ring-offset-surface-raised')}
            style={{ background: s }} />
        ))}
      </div>
      {eyedropper && (
        <button type="button" onClick={() => void pickFromScreen()} className="flex h-7 items-center justify-center gap-2 rounded-control border border-border text-sm text-text-muted hover:bg-surface-hover hover:text-text">
          <Pipette className="size-3.5" /> Pick from screen
        </button>
      )}
    </div>
  )
  if (compact) return <Popover trigger={swatch} className="w-60">{panel}</Popover>
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <Input
        id={id ?? f?.id}
        value={text}
        disabled={disabled}
        spellCheck={false}
        maxLength={7}
        className="font-mono text-sm uppercase"
        prefix={<Popover trigger={swatch} className="w-60">{panel}</Popover>}
        wrapperClassName="[&>span:first-child]:pointer-events-auto [&>span:first-child]:pl-1"
        invalid={(!focused && !!draftError) || undefined}
        {...{ [INVALID_DRAFT_ATTR]: draftError ?? undefined }}
        onFocus={() => { focusedRef.current = true; setFocused(true) }}
        onChange={e => {
          const t = e.target.value
          setText(t)
          if (HEX6.test(t.trim())) { const h = normalizeHex(t)!; if (h !== normalizeHex(value ?? '')) onChange(h) }
        }}
        onKeyDown={e => { if (isCommitKey(e) && !commitText() && e.key === 'Enter' && draftError) e.preventDefault() }}
        onBlur={() => {
          focusedRef.current = false; setFocused(false)
          if (!commitText()) setText(value ?? '')
        }}
      />
    </div>
  )
}
